import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  LanguageService,
  ScreenService,
  ThemingService,
  languages,
} from '@catch/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, interval } from 'rxjs';
import { audit } from 'rxjs/operators';
import { illustration, zoomInOutUpAbsolute } from '../../../assets/animations/animations';
import { environment } from '../../../environments/environment';
import { JobsAdapter } from '../../adapters/jobs.adapter';
import { UserAdapter } from '../../adapters/user.adapter';
import { AppState } from '../../app.reducer';
import { CompanyUserRole } from '../../enums/company-user.role.enum';
import { CompanyUserTodo } from '../../interfaces/company-user-todo';
import { Notification } from '../../interfaces/notification';
import { User } from '../../interfaces/user';
import { UserAccount } from '../../interfaces/user-account';
import { FontawesomeService } from '../../services/fontawesome.service';
import { JobsService } from '../../services/company/jobs.service';
import { logout } from '../../store/auth/auth.actions';
import { hasPermission } from '../../store/auth/auth.reducer';
import { toggleSidenav } from '../../store/config/config.actions';
import {
  closeDownloadsDropdown,
  loadExportDetails,
  openDownloadsDropdown,
  removeExport,
  updateExportCreationTime,
  type ExportRequest,
} from '../../store/export-downloads/export-downloads.actions';
import {
  selectAllExports,
  selectExportsCount,
  selectReadyExports,
  selectShowDownloads,
} from '../../store/export-downloads/export-downloads.reducer';
import { loadNotificationsLazy, loadNotificationsPolling, markAllNotificationsAsSeen } from '../../store/notifications/notifications.actions';
import { selectNotificationsAll, selectNotificationsLoading, selectNotificationsUnseen } from '../../store/notifications/notifications.reducer';
import { selectSubscription } from '../../store/subscription/subscription.reducer';
import {
  deleteAllTodos as deleteAllTodosAction,
  deleteTodo as deleteTodoAction,
  loadTodosPolling,
  markAllTodosAsDone,
  toggleTodoState as toggleTodoStateAction,
} from '../../store/todos/todos.actions';
import { selectTodosAllReversed, selectTodosOpenOverall } from '../../store/todos/todos.reducer';
import { loadUserAccount } from '../../store/user-account/user-account.actions';
import { selectUserAccount } from '../../store/user-account/user-account.reducer';
import { loadUserAvatar } from '../../store/user-avatar/user-avatar.actions';
import { selectUserAvatarUrl } from '../../store/user-avatar/user-avatar.reducer';
import { loadUsers } from '../../store/users/users.actions';
import { selectUsersAll } from '../../store/users/users.reducer';
import { ChevronState } from '../rotating-chevron/rotating-chevron.component';
import { ViewHeaderComponent } from '../view-header/view-header.component';
import { CompanionComponent } from '../companion/companion.component';

@UntilDestroy()
@Component({
  selector: 'app-topnav',
  templateUrl: './topnav.component.html',
  styleUrls: ['./topnav.component.scss'],
  animations: [zoomInOutUpAbsolute, illustration],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    /*
    AsyncPipe,
    DatePipe,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    TranslocoPipe,
    FaIconComponent,
    NgSelectComponent,
    TooltipDirective,
    ClickOutsideDirective,
    TagComponent,
    ConfirmModalComponent,
    SpinnerComponent,
    SearchInputComponent,
    NotificationsComponent,
    RotatingChevronComponent,
    ScrollEndDirective,
    ScrollEndRootDirective,
    CreditsCounterComponent,
    CreditsCounterExpandedComponent,
    DropdownComponent,
    ReleaseNotesModalComponent,
    TourAnchorMatMenuDirective,
    */
    CompanionComponent,
    ViewHeaderComponent,
  ],
})
export class TopnavComponent implements OnInit {
  protected readonly ChevronState = ChevronState;
  showSearch: boolean = false;
  showTodos: boolean = false;
  showNotifications: boolean = false;
  showUserDropdown: boolean = false;
  languages = languages.filter((lang) => !lang.value.includes('fr'));

  userAccount$!: Observable<UserAccount>;
  allUsers$!: Observable<User[]>;

  notifications$!: Observable<Notification[]>;
  notificationsLoading$!: Observable<boolean>;
  notificationsUnseen$!: Observable<number>;

  todos$!: Observable<CompanyUserTodo[]>;
  todosLoading$!: Observable<boolean>;
  todosOpenOverall$!: Observable<number>;
  hasAdminPermission$!: Observable<boolean>;
  showDownloads$!: Observable<boolean>;
  allExports$!: Observable<Array<ExportRequest & { isPending: boolean }>>;
  exportsCount$!: Observable<number>;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly store = inject(Store<AppState>);
  private readonly userAdapter = inject(UserAdapter);
  private readonly jobsAdapter = inject(JobsAdapter);
  private readonly jobsService = inject(JobsService);
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly screenService = inject(ScreenService);
  public readonly themingService = inject(ThemingService);

  subscription = this.store.selectSignal(selectSubscription);

  avatarUrl$!: Observable<string>;

  ngOnInit(): void {
    this.store.dispatch(loadUserAccount());
    this.userAccount$ = this.store.select(selectUserAccount);

    this.store.dispatch(loadNotificationsPolling());
    this.store.dispatch(loadTodosPolling());
    this.notifications$ = this.store.select(selectNotificationsAll);
    this.notificationsLoading$ = this.store.select(selectNotificationsLoading).pipe(
      audit((isLoading) => (isLoading ? interval(0) : interval(500))) // Show loading spinner atleast 500ms
    );
    this.notificationsUnseen$ = this.store.select(selectNotificationsUnseen);

    this.todos$ = this.store.select(selectTodosAllReversed);
    this.todosLoading$ = this.store.select(selectNotificationsLoading).pipe(
      audit((isLoading) => (isLoading ? interval(0) : interval(500))) // Show loading spinner atleast 500ms
    );
    this.todosOpenOverall$ = this.store.select(selectTodosOpenOverall);
    this.hasAdminPermission$ = this.store.select(hasPermission(CompanyUserRole.ADMIN));
    this.showDownloads$ = this.store.select(selectShowDownloads);
    this.allExports$ = this.store.select(selectAllExports);
    this.exportsCount$ = this.store.select(selectExportsCount);

    this.store.dispatch(loadUserAvatar());
    this.avatarUrl$ = this.store.select(selectUserAvatarUrl);

    this.store.dispatch(loadUsers());
    this.allUsers$ = this.store.select(selectUsersAll);

    this.router.events.subscribe({
      next: (event) => {
        if (event instanceof NavigationStart) {
          this.showUserDropdown = false;
          this.cdr.markForCheck();
        }
      },
    });

    this.screenService.isMobile$.pipe(untilDestroyed(this)).subscribe(() => this.cdr.markForCheck());
  }

  logout() {
    this.store.dispatch(logout());
  }

  getAvatarURL(applicationID: number) {
    return `${environment.apiBaseUrl}/company/applications/${applicationID}/avatar`;
  }

  setLanguage(value: string) {
    this.languageService.setLanguage(value).then(() => {
      this.userAdapter.setLanguage().subscribe(() => this.cdr.markForCheck());
    });
  }

  loadMoreNotifications() {
    this.store.dispatch(loadNotificationsLazy());
  }

  markAllNotificationsAsSeen() {
    this.store.dispatch(markAllNotificationsAsSeen());
  }

  toggleTodoState(todo: CompanyUserTodo) {
    this.store.dispatch(toggleTodoStateAction({ todo }));
  }

  deleteTodo(todo: CompanyUserTodo) {
    this.store.dispatch(deleteTodoAction({ todo }));
  }

  getTodoApplicantName(todo: CompanyUserTodo): string {
    return `${todo.firstName} ${todo.lastName}`.trim();
  }

  getInitials(firstName?: string, lastName?: string): string {
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  }

  getUserInitials(userAccount?: UserAccount | null): string {
    return this.getInitials(userAccount?.firstName, userAccount?.lastName);
  }

  getTodoDescription(todo: CompanyUserTodo): string {
    if (todo.jobId) {
      const jobTitle = this.jobsService.getJobById(todo.jobId)?.title;

      if (jobTitle) {
        return jobTitle;
      }
    }

    return todo.processStepName;
  }

  getTodoRoute(todo: CompanyUserTodo): string[] | null {
    if (todo.jobId) {
      return ['/jobs', String(todo.jobId), 'applications', String(todo.applicationId)];
    }

    return ['/talents/applications', String(todo.applicationId)];
  }

  deleteAllTodos() {
    this.store.dispatch(deleteAllTodosAction());
  }

  markAllTodosAsDone() {
    this.store.dispatch(markAllTodosAsDone());
  }

  toggleSidenav() {
    this.store.dispatch(toggleSidenav());
  }

  navigateToNotificationSettings() {
    this.router.navigate(['/settings/profile/notifications']);
  }

  downloadExport(exportItem: ExportRequest) {
    this.jobsAdapter
      .getExportStatus(exportItem.jobId, exportItem.exportId)
      .subscribe({
        next: (status) => {
          if (status.downloadUrl) {
            window.open(status.downloadUrl, '_blank');
          }
          if (status.creationTime) {
            this.store.dispatch(
              updateExportCreationTime({
                exportId: exportItem.exportId,
                creationTime: status.creationTime,
              })
            );
          }
          this.store.dispatch(removeExport({ exportId: exportItem.exportId }));
          this.store.dispatch(closeDownloadsDropdown());
        },
      });
  }

  removeExport(exportItem: ExportRequest) {
    this.store.dispatch(removeExport({ exportId: exportItem.exportId }));
  }

  openDownloadsDropdown() {
    const count = this.store.selectSignal(selectExportsCount)();
    if (count > 0) {
      this.store.dispatch(openDownloadsDropdown());
      const exports = this.store.selectSignal(selectReadyExports)();
      const withoutCreationTime = exports.filter((e) => !e.creationTime);
      if (withoutCreationTime.length > 0) {
        this.store.dispatch(loadExportDetails({ exports: withoutCreationTime }));
      }
    }
  }

  closeDownloadsDropdown() {
    this.store.dispatch(closeDownloadsDropdown());
  }
}
